import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ICurrency, ICustomer, IFile, ILocation, IMainContact, IPayment_term } from 'src/app/features/customers/models/customers.models';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { IObjectCustomFields } from 'src/app/features/settings/models/settings.models';
import { ICompanyEmail, IStructuredApiError } from 'src/app/features/knowledge-base/models/customer-email.models';
import { ILocationEmail } from 'src/app/features/knowledge-base/models/location-email.models';
import { TranslateService } from '@ngx-translate/core';
import { ILocationCodeTag, locationCodeTags } from 'src/app/shared/utils/location-codes.util';

type ExtraEmailSource = 'erp' | 'volta';
type ExtraEmailKind = 'company' | 'location';
type EmailSourceFilter = 'all' | 'erp' | 'volta';
/** 'all' | 'company' | a location id — drives the scope dropdown. */
type EmailScopeFilter = 'all' | 'company' | number;

interface MergedEmailRow {
  id: number;
  email: string;
  source: ExtraEmailSource;
  kind: ExtraEmailKind;
  locationId: number | null;
  locationName: string | null;
}

/**
 * Pure presentation component for the right-side panel of the customer detail view.
 * Renders two tabs: "Details" (company info, addresses, conditions) and "Contacts"
 * (location-grouped contact list + a single merged "Extra emails" registry).
 *
 * The component class has no store dependencies and makes no HTTP calls — all data is
 * passed via @Input bindings. Actions (add/edit/delete contact or email, edit files, etc.)
 * are emitted upward via @Output so the parent (RecapLocationComponent) can dispatch
 * the appropriate NgRx actions. The merged email list shows company-level emails and the
 * emails of every location under the company together; each emitted email action carries a
 * `kind` (and, for adds, an optional location id) so the parent can route it to the right
 * endpoint. The one in-template store touch is the shared `*appHasPermission` directive that
 * gates the email mutation controls on `edit:company`.
 */
@Component({
  selector: 'app-customer-informations',
  templateUrl: './customer-informations.component.html',
  styleUrls: ['./customer-informations.component.scss']
})
export class CustomerInformationsComponent implements OnChanges {
  @Input() customer!: ICustomer;
  @Input() currentLocation!: ILocation;
  @Input() contacts_information: IMainContact[] = [];
  @Input() otherLocations: ILocation[] = [];
  @Input() currencies: ICurrency[] = [];
  @Input() paymentTerms: IPayment_term[] = [];
  @Input() groupes: ICompaniesGroups[] = [];
  @Input() tags: ITag[] = [];
  @Input() categories: ICategory[] = [];
  @Input() blacklist_brands: IBrand[] = [];
  @Input() favorit_brands: IBrand[] = [];
  @Input() selectedFiles: IFile[] = [];
  @Input() objectCustomFields: IObjectCustomFields | null = null;
  @Input() currentLanguage: string = 'en';
  @Input() visibleTab: string = 'details';
  @Input() aiEmailsErp: ICompanyEmail[] = [];
  @Input() aiEmailsVolta: ICompanyEmail[] = [];
  @Input() locationEmailsErp: ILocationEmail[] = [];
  @Input() locationEmailsVolta: ILocationEmail[] = [];
  @Input() isLoadingEmails: boolean = false;
  @Input() emailMutationError: IStructuredApiError | null = null;
  @Input() traditionalContactsEnabled: boolean = false;

  selectedConversationId: number | null = null;

  /** The shown location's identifier codes, one tag per value. */
  taxCodeTags: ILocationCodeTag[] = [];

  // Single inline-edit/add/delete state for the merged email list.
  newEmailInput: string = '';
  newEmailLocationId: number | null = null;
  editingEmailId: number | null = null;
  editingEmailValue: string = '';
  editingEmailKind: ExtraEmailKind | null = null;
  editingEmailLocationId: number | null = null;
  confirmingDeleteEmailId: number | null = null;
  confirmingDeleteEmailKind: ExtraEmailKind | null = null;
  confirmingDeleteEmailLocationId: number | null = null;
  emailError: string = '';
  addEmailError: string = '';
  showAddEmailForm: boolean = false;
  sourceFilter: EmailSourceFilter = 'all';
  scopeFilter: EmailScopeFilter = 'all';

  @Output() addContactClicked = new EventEmitter<IMainContact | undefined>();
  @Output() editFilesClicked = new EventEmitter<void>();
  @Output() editCustomFieldsClicked = new EventEmitter<void>();
  @Output() openLocationClicked = new EventEmitter<ILocation>();
  @Output() addLocationsClicked = new EventEmitter<void>();
  @Output() downloadFileClicked = new EventEmitter<IFile>();
  @Output() previewFileClicked = new EventEmitter<{ data: string; content_type: string }>();

  @Output() editLocationClicked = new EventEmitter<ILocation>();

  @Output() addEmailClicked = new EventEmitter<{ email: string; locationId: number | null }>();
  @Output() editEmailClicked = new EventEmitter<{ id: number; email: string; kind: ExtraEmailKind; locationId: number | null }>();
  @Output() deleteEmailClicked = new EventEmitter<{ id: number; kind: ExtraEmailKind; locationId: number | null }>();

  constructor(private translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentLocation'] || changes['contacts_information'] || changes['otherLocations']) {
      this.rebuildContactsGroups();
    }
    if (changes['currentLocation']) {
      // Built here rather than in the template: a fresh array on every change
      // detection run would re-create the tags each tick.
      this.taxCodeTags = locationCodeTags(this.currentLocation);
    }
    if (changes['customer'] || changes['visibleTab']) {
      this.resetEmailInlineState();
    }
  }

  private rebuildContactsGroups(): void {
    const groups: { location: ILocation; contacts: IMainContact[]; isCurrent: boolean }[] = [];
    if (this.currentLocation) {
      groups.push({ location: this.currentLocation, contacts: this.contacts_information ?? [], isCurrent: true });
    }
    for (const loc of (this.otherLocations ?? [])) {
      groups.push({ location: loc, contacts: loc.contacts_information ?? [], isCurrent: false });
    }
    this.contactsGroups = groups;
    this.totalContactsCount = groups.reduce((sum, g) => sum + g.contacts.length, 0);
    if (this.currentLocation) {
      this.openGroupKeys.add(this.currentLocation.id ?? this.currentLocation.name ?? '');
    }
    this.applyContactFilter();
  }

  trackByLocationId = (_: number, group: { location: ILocation }): number | string =>
    group.location.id ?? group.location.name ?? '';

  trackByContactId = (_: number, contact: IMainContact): number | string =>
    contact.id ?? contact.email ?? '';

  isGroupOpen(group: { location: ILocation }): boolean {
    return this.openGroupKeys.has(group.location.id ?? group.location.name ?? '');
  }

  toggleGroup(key: string | number): void {
    if (this.openGroupKeys.has(key)) {
      this.openGroupKeys.delete(key);
    } else {
      this.openGroupKeys.add(key);
    }
  }

  getInitials(contact: IMainContact): string {
    const f = contact.contact_fname?.[0]?.toUpperCase() ?? '';
    const l = contact.contact_lname?.[0]?.toUpperCase() ?? '';
    return f + l || '?';
  }

  setContactSearch(query: string): void {
    this.contactSearchQuery = query;
    this.applyContactFilter();
  }

  /** Keep group when location name matches, or when any contact name/email matches. */
  applyContactFilter(): void {
    const q = this.contactSearchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredContactsGroups = this.contactsGroups;
      return;
    }
    this.filteredContactsGroups = this.contactsGroups
      .map(g => ({
        ...g,
        contacts: g.contacts.filter(c =>
          `${c.contact_fname} ${c.contact_lname}`.toLowerCase().includes(q) ||
          (c.email?.toLowerCase().includes(q) ?? false)
        ),
      }))
      .filter(g => g.location.name.toLowerCase().includes(q) || g.contacts.length > 0);
  }

  get allLocations(): ILocation[] {
    const locations: ILocation[] = [];
    if (this.currentLocation) locations.push(this.currentLocation);
    if (this.otherLocations?.length) locations.push(...this.otherLocations);
    return locations;
  }

  contactsGroups: { location: ILocation; contacts: IMainContact[]; isCurrent: boolean }[] = [];
  filteredContactsGroups: { location: ILocation; contacts: IMainContact[]; isCurrent: boolean }[] = [];
  totalContactsCount = 0;
  openGroupKeys: Set<string | number> = new Set();
  contactSearchQuery = '';

  /** Payment term names are translation keys stored by the backend. */
  getPaymentTermName(termId: number): string {
    const term = this.paymentTerms.find(t => t.id === termId);
    return term ? this.translateService.instant(term.name) : '--';
  }

  getCurrencyName(currencyId: number): string {
    const c = this.currencies.find(cur => cur.id === +currencyId);
    return c ? `${c.name} (${c.symbol})` : '--';
  }

  /** Uses `apartment` as the formatted address line from Google Maps. */
  formatShippingAddress(loc: ILocation): string {
    const parts = [loc.apartment, loc.zip, loc.city].filter(Boolean);
    const country = loc.country?.name;
    if (country && parts.length) return `${parts.join(', ')} - ${country}`;
    if (country) return country;
    return parts.join(', ') || '--';
  }

  formatBillingAddress(loc: ILocation): string {
    const parts = [loc.billing_apartment, loc.billing_zip, loc.billing_city].filter(Boolean);
    const country = loc.billing_country?.name;
    if (country && parts.length) return `${parts.join(', ')} - ${country}`;
    if (country) return country;
    return parts.join(', ') || '--';
  }

  getMainLocation(): ILocation | undefined {
    return this.allLocations.find(l => l.is_main);
  }

  /** Location option label: name + shipping address, like the companies list. */
  locationOptionLabel(loc: ILocation): string {
    const address = this.formatShippingAddress(loc);
    return address && address !== '--' ? `${loc.name} — ${address}` : loc.name;
  }

  getMainContact(): IMainContact | undefined {
    return this.contacts_information?.find(c => c.is_user) ?? this.contacts_information?.[0];
  }

  // ── Extra emails (merged company + locations) ───────────────────────────────

  validateEmail(email: string): boolean {
    const pattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email.trim());
  }

  private resetEmailInlineState(): void {
    this.editingEmailId = null;
    this.editingEmailKind = null;
    this.editingEmailLocationId = null;
    this.editingEmailValue = '';
    this.confirmingDeleteEmailId = null;
    this.confirmingDeleteEmailKind = null;
    this.confirmingDeleteEmailLocationId = null;
    this.showAddEmailForm = false;
    this.newEmailInput = '';
    this.newEmailLocationId = null;
    this.emailError = '';
    this.addEmailError = '';
  }

  /** Resolve a location's display name from the inputs the parent already passes. */
  private locationName(locationId: number): string | null {
    return this.allLocations.find(l => l.id === locationId)?.name ?? null;
  }

  /** All rows (company + every location), newest-source-agnostic, before filtering. */
  get mergedRows(): MergedEmailRow[] {
    const companyRows: MergedEmailRow[] = [
      ...this.aiEmailsErp.map(item => this.toCompanyRow(item, 'erp')),
      ...this.aiEmailsVolta.map(item => this.toCompanyRow(item, 'volta')),
    ];
    const locationRows: MergedEmailRow[] = [
      ...this.locationEmailsErp.map(item => this.toLocationRow(item, 'erp')),
      ...this.locationEmailsVolta.map(item => this.toLocationRow(item, 'volta')),
    ];
    return [...companyRows, ...locationRows];
  }

  private toCompanyRow(item: ICompanyEmail, source: ExtraEmailSource): MergedEmailRow {
    return { id: item.id, email: item.email, source, kind: 'company', locationId: null, locationName: null };
  }

  private toLocationRow(item: ILocationEmail, source: ExtraEmailSource): MergedEmailRow {
    return {
      id: item.id,
      email: item.email,
      source,
      kind: 'location',
      locationId: item.location_id,
      locationName: this.locationName(item.location_id),
    };
  }

  get visibleEmails(): MergedEmailRow[] {
    return this.mergedRows.filter(row => this.matchesSource(row) && this.matchesScope(row));
  }

  private matchesSource(row: MergedEmailRow): boolean {
    return this.sourceFilter === 'all' || row.source === this.sourceFilter;
  }

  private matchesScope(row: MergedEmailRow): boolean {
    if (this.scopeFilter === 'all') return true;
    if (this.scopeFilter === 'company') return row.kind === 'company';
    return row.kind === 'location' && row.locationId === this.scopeFilter;
  }

  get totalEmailsCount(): number {
    return this.mergedRows.length;
  }

  get erpEmailsCount(): number {
    return this.aiEmailsErp.length + this.locationEmailsErp.length;
  }

  get voltaEmailsCount(): number {
    return this.aiEmailsVolta.length + this.locationEmailsVolta.length;
  }

  get scopeFilterLabel(): string {
    if (this.scopeFilter === 'all') {
      return this.translateService.instant('common.all');
    }
    if (this.scopeFilter === 'company') {
      return this.translateService.instant('customers.contacts_tab.email_scope_company');
    }
    return this.locationName(this.scopeFilter)
      ?? this.translateService.instant('customers.contacts_tab.email_scope_location');
  }

  get emailEmptyMessage(): string {
    return this.translateService.instant('customers.contacts_tab.email_empty_all');
  }

  setSourceFilter(filter: EmailSourceFilter): void {
    this.sourceFilter = filter;
  }

  setScopeFilter(scope: EmailScopeFilter): void {
    this.scopeFilter = scope;
  }

  toggleAddEmailForm(): void {
    this.showAddEmailForm = !this.showAddEmailForm;
    this.newEmailInput = '';
    this.newEmailLocationId = null;
    this.addEmailError = '';
  }

  /** Empty location → company email; a chosen location → that location's email. */
  addManualEmail(): void {
    const email = this.newEmailInput.trim();
    if (!email) {
      this.addEmailError = this.translateService.instant('customers.contacts_tab.email_required');
      return;
    }
    if (!this.validateEmail(email)) {
      this.addEmailError = this.translateService.instant('customers.contacts_tab.invalid_email_format');
      return;
    }
    this.addEmailClicked.emit({ email, locationId: this.newEmailLocationId });
    this.newEmailInput = '';
    this.newEmailLocationId = null;
    this.addEmailError = '';
    this.showAddEmailForm = false;
  }

  /** Clicking an editable (volta) row opens the inline editor; erp rows are read-only. */
  startEditEmail(row: MergedEmailRow): void {
    if (row.source !== 'volta') return;
    this.editingEmailId = row.id;
    this.editingEmailKind = row.kind;
    this.editingEmailLocationId = row.locationId;
    this.editingEmailValue = row.email;
    this.emailError = '';
    this.confirmingDeleteEmailId = null;
    this.confirmingDeleteEmailKind = null;
    this.confirmingDeleteEmailLocationId = null;
  }

  confirmEditEmail(): void {
    const email = this.editingEmailValue.trim();
    if (!email) {
      this.emailError = this.translateService.instant('customers.contacts_tab.email_required');
      return;
    }
    if (!this.validateEmail(email)) {
      this.emailError = this.translateService.instant('customers.contacts_tab.invalid_email_format');
      return;
    }
    if (this.editingEmailId !== null && this.editingEmailKind !== null) {
      this.editEmailClicked.emit({
        id: this.editingEmailId,
        email,
        kind: this.editingEmailKind,
        locationId: this.editingEmailLocationId,
      });
    }
    this.editingEmailId = null;
    this.editingEmailKind = null;
    this.editingEmailLocationId = null;
    this.editingEmailValue = '';
    this.emailError = '';
  }

  cancelEditEmail(): void {
    this.editingEmailId = null;
    this.editingEmailKind = null;
    this.editingEmailLocationId = null;
    this.editingEmailValue = '';
    this.emailError = '';
  }

  requestDeleteEmail(row: MergedEmailRow): void {
    if (row.source !== 'volta') return;
    this.confirmingDeleteEmailId = row.id;
    this.confirmingDeleteEmailKind = row.kind;
    this.confirmingDeleteEmailLocationId = row.locationId;
    this.editingEmailId = null;
    this.editingEmailKind = null;
    this.editingEmailLocationId = null;
    this.editingEmailValue = '';
    this.emailError = '';
  }

  confirmDeleteEmail(): void {
    if (this.confirmingDeleteEmailId !== null && this.confirmingDeleteEmailKind !== null) {
      this.deleteEmailClicked.emit({
        id: this.confirmingDeleteEmailId,
        kind: this.confirmingDeleteEmailKind,
        locationId: this.confirmingDeleteEmailLocationId,
      });
    }
    this.confirmingDeleteEmailId = null;
    this.confirmingDeleteEmailKind = null;
    this.confirmingDeleteEmailLocationId = null;
  }

  cancelDeleteEmail(): void {
    this.confirmingDeleteEmailId = null;
    this.confirmingDeleteEmailKind = null;
    this.confirmingDeleteEmailLocationId = null;
  }

  trackEmail = (_: number, row: MergedEmailRow): string => `${row.kind}:${row.source}:${row.id}`;
}
